import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import LabeledSlider from 'ui-library/inputs/LabeledSlider';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import jsonValueToReadable from 'util/jsonValueToReadable';

import {
  PredictionParameter,
  PredictionParameterNumericInput,
  PredictionParameterValue,
} from './PredictionParametersTypes';

/** @package */
export const SLIDER_NUM_STEPS = 100;

/** @package */
export function parameterValueToSliderValue(parameterInput: PredictionParameterNumericInput): number {
  const stepSize = (parameterInput.max - parameterInput.min) / SLIDER_NUM_STEPS;
  return Math.round((parameterInput.value - parameterInput.min) / stepSize);
}

export interface PredictionParametersProps {
  parameters: Array<PredictionParameter>;
  onChangeValue: (systemName: string, value: PredictionParameterValue) => void;
}

export default function PredictionParameters({
  parameters,
  onChangeValue,
}: PredictionParametersProps): JSX.Element {
  return (
    <>
      {parameters.map((parameter, index) => (
        <div
          key={parameter.systemName}
          data-test-component="PredictionParameters"
          data-test-element="input"
          className={classNames(index > 0 && 'u-input-spacing-top')}
        >
          <div className={css(styles.parameterName)}>
            {parameter.displayName}
            {parameter.valueInput.isDropdown ? '' : `: ${prettyChartNumber(parameter.valueInput.value)}`}
          </div>
          {parameter.valueInput.isDropdown ? (
            <SelectDropdown
              displayName={parameter.valueInput.value}
              menu={
                <SearchableMenu
                  height={MenuHeight.LARGE}
                  width={MenuWidth.FULL}
                  selectedValue={parameter.valueInput.value}
                  onSelect={(value) => {
                    onChangeValue(parameter.systemName, value);
                  }}
                  optionDatas={parameter.valueInput.options.map((value) => ({
                    name: jsonValueToReadable(value),
                    value: jsonValueToReadable(value),
                  }))}
                />
              }
            />
          ) : (
            // LabeledSlider doesn't support arbitrary floats well, so instead give it integer steps, and
            // translate those steps back into the actual range of the parameters here
            <LabeledSlider
              min={0}
              minLabel={prettyChartNumber(parameter.valueInput.min)}
              max={SLIDER_NUM_STEPS}
              maxLabel={prettyChartNumber(parameter.valueInput.max)}
              onChange={(sliderValue) => {
                const input = parameter.valueInput as PredictionParameterNumericInput;
                onChangeValue(
                  parameter.systemName,
                  ((input.max - input.min) / SLIDER_NUM_STEPS) * sliderValue + input.min
                );
              }}
              step={1}
              value={parameterValueToSliderValue(parameter.valueInput)}
            />
          )}
        </div>
      ))}
    </>
  );
}

const styles = StyleSheet.create({
  parameterName: FONT_PROXIMA_TITLE,
});
